// src/app/pages/campaigns/Leads.jsx
import { useState, useEffect, useRef } from "react";
import axios from "../../lib/axios";
import { useParams } from "react-router-dom";
import Tabs from "./Tabs";
import Papa from "papaparse";
import { Upload, X, Users } from "lucide-react";
import LeadEnrichmentSuggestion from "./LeadEnrichmentSuggestion";

export default function Leads() {
  const { id } = useParams();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [customKeys, setCustomKeys] = useState([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [enrichmentSuggestion, setEnrichmentSuggestion] = useState(null);

  // CSV upload states
  const fileInputRef = useRef(null);
  const [columns, setColumns] = useState([]);
  const [uploadedRows, setUploadedRows] = useState([]);

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const closeModal = () => {
    setShowModal(false);
    setColumns([]);
    setUploadedRows([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const triggerFile = () => fileInputRef.current.click();

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    // Reset value so the same file can be re-selected after a change
    event.target.value = "";

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data;
        setUploadedRows(data);

        if (data.length > 0) {
          const headers = Object.keys(data[0]);

          const columnsWithSamples = headers.map((header) => ({
            name: header,
            samples: data
              .slice(0, 4)
              .map((row) => (row[header] ? row[header].toString().trim() : "")),
            selectedType: "Do not import",
          }));

          setColumns(columnsWithSamples);
        }
      },
    });
  };

  const triggerLeadEnrichment = async (rows) => {
    try {
      // Find the email column from the mapping
      const emailCol = columns.find(c => c.selectedType === "Email");
      if (!emailCol) return;

      // Sample first 20 rows only
      const emails = rows
        .slice(0, 20)
        .map(row => row[emailCol.name])
        .filter(Boolean);

      if (emails.length === 0) return;

      const res = await axios.post("ai/enrich-leads", { emails });
      if (res.data) {
        setEnrichmentSuggestion(res.data);
      }
    } catch {
      // Silently fail — non-blocking
    }
  };

  const handleApplyTimezone = async (timezone) => {
    try {
      await axios.post(`email-campaign/schedule/${id}`, { timezone });
    } catch {
      // Silently fail
    } finally {
      setEnrichmentSuggestion(null);
    }
  };

  const fetchLeads = () => {
    if (!id) return;
    setLoading(true);
    setError("");
    axios
      .get(`contacts/get-leads/${id}`)
      .then((res) => {
        const data = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data.leads)
          ? res.data.leads
          : Array.isArray(res.data.data)
          ? res.data.data
          : [];
        setLeads(data);
        if (data.length > 0 && data[0].custom_fields) {
          setCustomKeys(Object.keys(data[0].custom_fields));
        }
      })
      .catch((e) => {
        setError(e.response?.data?.message || "Failed to load leads");
        setLeads([]);
      })
      .finally(() => setLoading(false));
  };

  const handleSubmitCSV = async () => {
    try {
      const payload = {
        rows: uploadedRows,
        mapping: columns.map(({ name, selectedType }) => ({
          column: name,
          mappedTo: selectedType,
        })),
      };

      await axios.post(`contacts/create-bulk/${id}`, payload);

      const rowsToEnrich = uploadedRows;
      closeModal();

      // Refresh leads table
      fetchLeads();

      // Trigger enrichment after successful import (non-blocking)
      triggerLeadEnrichment(rowsToEnrich);
    } catch (err) {
      console.log(err);
      alert("Error uploading contacts");
    }
  };

  const getUsedTypes = () =>
    columns
      .map((c) => c.selectedType)
      .filter((type) => type !== "Do not import" && type !== "Custom variable");

  const fieldOptions = [
    "Do not import",
    "Email",
    "Name",
    "Phone",
    "Location",
    "Linkedin",
    "Facebook",
    "Custom variable",
  ];

  return (
    <div className="page animate-fade-in">
      <Tabs activeTab="Leads" campaignId={id} />

      <div className="page-header">
        <div className="header-info">
          <h2>Campaign Leads</h2>
          <p>Manage and import your campaign contacts</p>
        </div>
        <div className="header-actions">
          <button onClick={() => setShowModal(true)} className="btn btn-primary">
            <Upload size={16} />
            Add Leads
          </button>
        </div>
      </div>

      <LeadEnrichmentSuggestion
        suggestion={enrichmentSuggestion}
        onApply={handleApplyTimezone}
        onDismiss={() => setEnrichmentSuggestion(null)}
      />

      <div className="card">
        <div className="card-header">
          <h3>
            <Users size={18} />
            All Leads ({leads.length})
          </h3>
        </div>

        {loading ? (
          <div style={{ padding: '3rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="animate-spin rounded-full h-12 w-12" style={{ border: '3px solid var(--color-border)', borderTopColor: 'var(--color-primary)' }}></div>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  {customKeys.map((key) => (
                    <th key={key}>{key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {leads.length > 0 ? (
                  leads.map((lead, i) => (
                    <tr key={lead.id ?? i}>
                      <td>{i + 1}</td>
                      <td>{lead.name}</td>
                      <td>{lead.email}</td>
                      <td>
                        <span className="status-badge" style={{
                          background: lead.is_bounced
                            ? 'rgba(249, 115, 22, 0.1)'
                            : lead.unsubscribed
                            ? 'rgba(239, 68, 68, 0.1)'
                            : 'rgba(34, 197, 94, 0.1)',
                          color: lead.is_bounced
                            ? '#f97316'
                            : lead.unsubscribed
                            ? '#ef4444'
                            : '#22c55e'
                        }}>
                          {lead.is_bounced ? "Bounced" : lead.unsubscribed ? "Unsubscribed" : "Active"}
                        </span>
                      </td>
                      {customKeys.map((key) => (
                        <td key={key} style={{ color: 'var(--color-text-secondary)' }}>
                          {lead.custom_fields?.[key] ?? "-"}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4 + customKeys.length} style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)' }}>
                      <Users size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                      <p>No leads found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal modal-lg">
            <div className="modal-header">
              <h3>Import Leads</h3>
              <button onClick={closeModal} className="icon-btn">
                <X size={20} />
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {columns.length === 0 ? (
              <div className="modal-body" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                <Upload size={48} style={{ margin: '0 auto 1rem', color: 'var(--color-text-muted)' }} />
                <p style={{ marginBottom: '1.5rem', color: 'var(--color-text-secondary)' }}>
                  Upload a CSV file to import leads
                </p>
                <button onClick={triggerFile} className="btn btn-primary">
                  <Upload size={16} />
                  Browse CSV
                </button>
              </div>
            ) : (
              <>
                <div className="modal-body" style={{ padding: '1rem 1.5rem' }}>
                  <div className="table-container" style={{ maxHeight: '55vh', overflow: 'auto' }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Column</th>
                          <th>Map To</th>
                          <th>Samples</th>
                        </tr>
                      </thead>
                      <tbody>
                        {columns.map((col, idx) => {
                          const usedTypes = getUsedTypes();
                          return (
                            <tr key={idx}>
                              <td style={{ fontWeight: 600 }}>{col.name}</td>
                              <td>
                                <select
                                  value={col.selectedType}
                                  onChange={(e) => {
                                    const newCols = [...columns];
                                    newCols[idx].selectedType = e.target.value;
                                    setColumns(newCols);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    background: 'var(--color-bg-primary)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-md)',
                                    color: 'var(--color-text-primary)'
                                  }}
                                >
                                  {fieldOptions.map((opt) => (
                                    <option
                                      key={opt}
                                      value={opt}
                                      disabled={
                                        usedTypes.includes(opt) &&
                                        col.selectedType !== opt &&
                                        opt !== "Do not import" &&
                                        opt !== "Custom variable"
                                      }
                                    >
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <div style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                                  {col.samples.map((s, i) => (
                                    <div key={i}>{s}</div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {error && <div className="error-message" style={{ marginTop: '1rem' }}>{error}</div>}
                </div>

                <div className="modal-footer">
                  <button
                    onClick={() => {
                      setColumns([]);
                      setUploadedRows([]);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="btn btn-ghost"
                  >
                    Change File
                  </button>
                  <button onClick={closeModal} className="btn btn-ghost">
                    Cancel
                  </button>
                  <button onClick={handleSubmitCSV} className="btn btn-primary">
                    Submit & Import
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
